/** Debug overlay showing a sidebar panel with per-enemy stats and world-space index labels. */
import Phaser from 'phaser';
import { Enemy } from '../../entities/Enemy';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { crispText, crispRect } from '../../ui/CrispUtil';
import { TextStyle, Hex, Color, DEBUG_WORLD_ALPHA } from '../../ui/Theme';
import { SF0_X, SF0_Y } from '../../utils/Constants';

const PANEL_X = SF0_X + 620;
const PANEL_Y = SF0_Y + 10;
const PANEL_WIDTH = 170;
const PANEL_LINE_HEIGHT = 11;
const PANEL_ENTRY_GAP = 5;
const LABEL_OFFSET_Y = -22;

/** Pooled sidebar panel with per-enemy stats and world-space index labels. Reuses objects to avoid GPU texture churn. */
export class InspectorOverlay {
  private scene: Phaser.Scene;

  // Panel chrome (recreated when enemy count changes)
  private bgRect: Phaser.GameObjects.Rectangle | null = null;
  private titleText: Phaser.GameObjects.Text | null = null;
  private lastPanelEnemyCount = 0;

  // Per-enemy pools: each "slot" has 5 panel texts + 1 swatch rect + 1 world label
  private panelTextPool: Phaser.GameObjects.Text[] = [];
  private swatchPool: Phaser.GameObjects.Rectangle[] = [];
  private worldLabelPool: Phaser.GameObjects.Text[] = [];
  private activeSlots = 0;
  private panelTextsPerSlot = 5;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Update the inspector panel and world-space index labels for all on-screen enemies. */
  draw(_graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    const cam = this.scene.cameras.main;
    const camLeft = cam.worldView.x;
    const camRight = camLeft + cam.worldView.width;
    const camTop = cam.worldView.y;
    const camBottom = camTop + cam.worldView.height;

    const visible: Enemy[] = [];
    for (const enemy of enemies) {
      if (enemy.sprite.x >= camLeft && enemy.sprite.x <= camRight
          && enemy.sprite.y >= camTop && enemy.sprite.y <= camBottom) {
        visible.push(enemy);
      }
    }

    if (visible.length === 0) {
      this.clear();
      return;
    }

    // Panel chrome — recreate when enemy count changes since crispRect
    // uses internal 2x scaling that setSize() doesn't account for
    const panelHeight = visible.length * (PANEL_LINE_HEIGHT * 5 + PANEL_ENTRY_GAP) + 20;
    if (this.bgRect && this.lastPanelEnemyCount !== visible.length) {
      this.bgRect.destroy();
      this.bgRect = null;
    }
    this.lastPanelEnemyCount = visible.length;
    if (!this.bgRect) {
      this.bgRect = crispRect(this.scene,
        PANEL_X + PANEL_WIDTH / 2, PANEL_Y + panelHeight / 2,
        PANEL_WIDTH, panelHeight, Color.black, 0.5
      ).setScrollFactor(0).setDepth(2000);
    } else {
      this.bgRect.setPosition(PANEL_X + PANEL_WIDTH / 2, PANEL_Y + panelHeight / 2);
      this.bgRect.setVisible(true);
    }

    if (!this.titleText) {
      this.titleText = crispText(this.scene, PANEL_X + 6, PANEL_Y + 4, 'ENEMIES',
        TextStyle.debugPanelTitle,
      ).setScrollFactor(0).setDepth(2001);
    } else {
      this.titleText.setVisible(true);
    }

    let py = PANEL_Y + 20;

    for (let i = 0; i < visible.length; i++) {
      const enemy = visible[i];
      const tag = enemy.instanceTag;
      const archColor = ConfigLoader.getArchetypeColor(enemy.config.id);
      const colorStr = '#' + archColor.toString(16).padStart(6, '0');

      // World label — shows stable instance tag matching log tags
      const worldLabel = this.getOrCreateWorldLabel(i);
      const tagStr = tag || '?';
      if (worldLabel.text !== tagStr) worldLabel.setText(tagStr);
      worldLabel.setPosition(enemy.sprite.x, enemy.sprite.y + LABEL_OFFSET_Y);
      if (worldLabel.style.color !== colorStr) worldLabel.setColor(colorStr);
      worldLabel.setVisible(true);

      // Swatch
      const swatch = this.getOrCreateSwatch(i);
      swatch.setPosition(PANEL_X + 10, py + 5);
      swatch.setFillStyle(archColor);
      swatch.setVisible(true);

      // Panel texts: header, HP, AI, aggro, pos (dirty-check to avoid GPU texture uploads)
      const base = i * this.panelTextsPerSlot;

      const header = this.getOrCreatePanelText(base);
      const headerStr = `${enemy.config.id}:${tag}`;
      if (header.text !== headerStr) header.setText(headerStr);
      header.setPosition(PANEL_X + 20, py);
      if (header.style.color !== colorStr) header.setColor(colorStr);
      header.setVisible(true);
      py += PANEL_LINE_HEIGHT;

      const hp = enemy.healthComponent;
      const hpStr = `HP: ${Math.ceil(hp.hp)}/${hp.max}`;
      const hpText = this.getOrCreatePanelText(base + 1);
      if (hpText.text !== hpStr) hpText.setText(hpStr);
      hpText.setPosition(PANEL_X + 20, py);
      if (hpText.style.color !== Hex.textSecondary) hpText.setColor(Hex.textSecondary);
      hpText.setVisible(true);
      py += PANEL_LINE_HEIGHT;

      const stateStr = enemy.ai.state === 'duty' ? `duty:${enemy.ai.dutySubstate}` : enemy.ai.state;
      const aiStr = `AI: ${stateStr}`;
      const stateText = this.getOrCreatePanelText(base + 2);
      if (stateText.text !== aiStr) stateText.setText(aiStr);
      stateText.setPosition(PANEL_X + 20, py);
      if (stateText.style.color !== Hex.textSecondary) stateText.setColor(Hex.textSecondary);
      stateText.setVisible(true);
      py += PANEL_LINE_HEIGHT;

      const aggroVal = enemy.ai.aggro;
      const aggroLabel = aggroVal <= 0 ? 'none' : aggroVal >= 100 ? 'MAX' : `${Math.round(aggroVal)}%`;
      const aggroColor = aggroVal <= 0 ? '#666666' : aggroVal >= 75 ? '#cc2222' : aggroVal >= 40 ? '#cccc22' : '#88cc22';
      const losTag = enemy.ai.hasLOS ? ' [LOS]' : '';
      const hwmTag = enemy.ai.aggroHWM > 0 ? ` hwm:${Math.round(enemy.ai.aggroHWM)}` : '';
      const aggroStr = `aggro: ${aggroLabel}${hwmTag}${losTag}`;
      const aggroText = this.getOrCreatePanelText(base + 3);
      if (aggroText.text !== aggroStr) aggroText.setText(aggroStr);
      aggroText.setPosition(PANEL_X + 20, py);
      if (aggroText.style.color !== aggroColor) aggroText.setColor(aggroColor);
      aggroText.setVisible(true);
      py += PANEL_LINE_HEIGHT;

      const posStr = `pos: ${Math.round(enemy.sprite.x)},${Math.round(enemy.sprite.y)}`;
      const extras: string[] = [];
      if (enemy.isKnockedBack) extras.push('KB');
      extras.push(`${Math.round(enemy.ai.distToPlayer)}px`);
      const posFullStr = `${posStr} [${extras.join(' ')}]`;
      const posText = this.getOrCreatePanelText(base + 4);
      if (posText.text !== posFullStr) posText.setText(posFullStr);
      posText.setPosition(PANEL_X + 20, py);
      if (posText.style.color !== Hex.textSecondary) posText.setColor(Hex.textSecondary);
      posText.setVisible(true);
      py += PANEL_LINE_HEIGHT + PANEL_ENTRY_GAP;
    }

    // Hide unused pool entries
    const newSlots = visible.length;
    for (let i = newSlots; i < this.activeSlots; i++) {
      this.worldLabelPool[i].setVisible(false);
      this.swatchPool[i].setVisible(false);
      const base = i * this.panelTextsPerSlot;
      for (let j = 0; j < this.panelTextsPerSlot; j++) {
        this.panelTextPool[base + j].setVisible(false);
      }
    }
    this.activeSlots = newSlots;
  }

  private getOrCreateWorldLabel(index: number): Phaser.GameObjects.Text {
    if (index < this.worldLabelPool.length) return this.worldLabelPool[index];
    const label = crispText(this.scene, 0, 0, '', {
      ...TextStyle.debugWorld, color: '#ffffff',
    }).setOrigin(0.5).setDepth(2001).setAlpha(DEBUG_WORLD_ALPHA);
    this.worldLabelPool.push(label);
    return label;
  }

  private getOrCreateSwatch(index: number): Phaser.GameObjects.Rectangle {
    if (index < this.swatchPool.length) return this.swatchPool[index];
    const swatch = crispRect(this.scene, 0, 0, 8, 8, 0xffffff)
      .setScrollFactor(0).setDepth(2001);
    this.swatchPool.push(swatch);
    return swatch;
  }

  private getOrCreatePanelText(index: number): Phaser.GameObjects.Text {
    if (index < this.panelTextPool.length) return this.panelTextPool[index];
    const text = crispText(this.scene, 0, 0, '', { ...TextStyle.debugPanel, color: Hex.textSecondary })
      .setScrollFactor(0).setDepth(2001);
    this.panelTextPool.push(text);
    return text;
  }

  /** Hide all panel elements and world-space labels. */
  clear(): void {
    if (this.bgRect) this.bgRect.setVisible(false);
    if (this.titleText) this.titleText.setVisible(false);
    for (let i = 0; i < this.activeSlots; i++) {
      this.worldLabelPool[i].setVisible(false);
      this.swatchPool[i].setVisible(false);
      const base = i * this.panelTextsPerSlot;
      for (let j = 0; j < this.panelTextsPerSlot; j++) {
        this.panelTextPool[base + j].setVisible(false);
      }
    }
    this.activeSlots = 0;
  }

  /** Destroy all pooled objects and free resources. */
  destroy(): void {
    this.bgRect?.destroy();
    this.bgRect = null;
    this.titleText?.destroy();
    this.titleText = null;
    for (const t of this.panelTextPool) t.destroy();
    this.panelTextPool = [];
    for (const s of this.swatchPool) s.destroy();
    this.swatchPool = [];
    for (const l of this.worldLabelPool) l.destroy();
    this.worldLabelPool = [];
    this.activeSlots = 0;
  }
}
